<template>
<div class="chapter">
<div class="inner_page">
<h1 class="cn"><a href="#/SadTaleoftheBr_toc#chap-3">III</a></h1>
<h1 class="ct"><a href="#/SadTaleoftheBr_toc#chap-3">Night in the Mountains</a></h1>
<p class="img"><img alt="art" src="images/Art_Pch.jpg"/></p>
<p class="cotx">Starting a fire in the dark on a windy mountain pass might daunt most, but to the Grossbarts it proved of little difficulty.
         While Manfried swore at the kindling Hegel gathered more wood, and when he made water he caught it in their dented cooking
         pot. He daubed his torn cheek and lip with his urine, wincing and adding more curses to the obstinate fire. Eventually the
         twigs caught, and by the growing light Hegel cut strips of cloth from the rattiest blanket and handed the pot to his brother.
      </p>
<p>Manfried remembered a barber mentioning horse piss was superior to that of a man and patiently waited over an hour until he
         heard the telltale sound and hurried to catch the precious stream. They knew only a little about the concept that melancholic,
         sanguine, choleric, and phlegmatic humours coursed through their bodies and determined their health, the Brothers instead
         sticking to simple quackery. The horse meat cooked slowly over the fresh coals, and Manfried set the pot beside it to heat
         the liquid. Hegel saw what his brother intended and cackled scornfully.
      </p>
<p>“Thinkin a Hamlin?” Hegel asked.</p>
<p>“Thinkin how fell that piss a yours stinks,” said Manfried, using a rag to apply the hot urine to his mangled ear.</p>
<p>“Shouldn’t use nuthin what comes from a beast,” said Hegel, taking a bite of meat.</p>
<p>“Yeah, cept the flesh you’s chewin, and that hide slung on your back.” Manfried snorted.</p>
<p>“It’s different. Beast gotta be dead to eat or wear it.”</p>
<p>“What bout feathers?” Manfried said after a pause.</p>
<p>“Feathers?”</p>
<p>“Feathers.”</p>
<p>“What’re you on bout?” Hegel scowled.</p>
<p>“Use feathers for arrows and combs and such, and the bird ain’t gotta be dead to take’em.”</p>
<p>“Course <em>that</em> don’t count,” Hegel guffawed. “Birds ain’t beasts.”
      </p>
<p>“Well… I suppose they’s a touch different.”</p>
<p>“Course they is. How many birds you see crawlin like a beast? Completely different. Same for fish. I’ll wrap some fish skin
         on me if I’s cut up, no question.”
      </p>
<p>Manfried nodded, not convinced but knowing the conversation could progress no further. The twins agreed on most matters, but
         even after all these years he could not fathom his brother’s distrust of four-legged creatures. Hegel certainly felt no aversion
         to eating or riding upon them, on the contrary, he took a pleasure from such things that Manfried correctly chalked up as
         sadistic. Dousing his ear in horse piss, Manfried splashed some on his sore neck for good measure.
      </p>
<p>Hegel felt splendid aside from his plethora of wounds. Chewing his dinner, he withdrew the murdered Gertie’s necklace from
         under his tunic and held it to the light. The rough carving would only be recognizable for what it represented by a truly
         devout individual, so crude and indistinct were Her angles. He rubbed the lump of the Virgin’s breasts with his thumb, and
         contemplated what it meant to be merciful.
      </p>
<p>Watching his brother, Manfried felt a twinge of jealousy. He considered himself far more pious than his brother, who had only
         taken to praising Her name after Manfried explained Her worth. Still, he reckoned, true mercy would be to allow his brother
         to keep his trophy rather than claiming it for himself. Even if he had been the one to slay the filthy heretic who originally
         wore it, his brother clearly took succor from Her. Inspiration arrived like a stinging gnat, and Manfried took one of the
         unused spears from the cart, broke the shaft, and began carving his own Virgin. His would be a more faithful representation,
         one with a larger chest and belly.
      </p>
<p>Eventually Hegel stretched out beside the fire and went to sleep, his brother standing watch. Manfried ate slowly, consuming
         several pounds of horse as the night wore on. He reflected on the fine meal, thinking with a satisfied smile that the days
         of rotting oats and badger meat were behind them. He knew the mountains could not stretch forever, and beyond them lay the
         sea, and passage to where their granddad’s wealth waited. After a spell he roused his brother to stand guard, and lay down
         on the patch of warm earth Hegel vacated. Manfried imagined the stars to be jewels shining in the depths of a long-sealed
         crypt and, drifting off, he almost glimpsed himself prying open the lid of night and stuffing his pocket with the glittering
         gems.
      </p>
<p>Heaping wood on the blaze and wrapping himself in another blanket, Hegel sat on a rock and wolfed down more meat. Splashing
         some water in the pot, he burned himself getting a bit of ash into it and scrubbed out the horse piss. He then filled it halfway
         with the last of their water, adding turnip pieces and hunks of meat. The stew simmered under Hegel’s watchful eye, the Grossbart
         also reflecting on their situation. He knew in his heart that for the first time in their lives they were truly on the road
         to riches.
      </p>
<p>While his brother dreamed of gold and sand and the Virgin, Hegel put his mind to their immediate wealth. Down the trail several
         dead horses waited for an industrious soul to turn them into headcheese, steaks, and pudding, to say nothing of the ligaments
         he could use to tie his shoes and the hide to be tanned for cloaks. Bones could be carved into fishhooks, a dried tail used
         to whip their carthorse. His mind turned over the possibilities when he remembered that there were dead men there as well.
      </p>
<p>Rather than feeling remorse at the blood they had spilled, Hegel groaned at their laziness in not searching the corpses immediately.
         In his mind each possessed pouches stuffed with coins, pouches that even now beasts dumbly bit off and swallowed or carried
         home to their nests. New shoes and hose dragged into dens, rings and bracelets rolling into rat holes. He took several steps
         down the trail, but without a sliver of moon he doubted even his keen eyes and sure feet could navigate the treacherous path.
         Instead he sat away from the fire, ears pricked for the sound of movement from the mountainside below. After hours of this
         futile exercise, he gently kicked his brother awake and lay back down.
      </p>
<p>Manfried awoke at dawn, his brother snoring beside him. The ashes were cold, indicating his slovenly brother had packed it
         in hours before. Cursing, he moved behind his brother and knelt down, putting his lips beside Hegel’s ear.
      </p>
<p>“Up!” Manfried hollered, startling both brother and horse awake.</p>
<p>“Eh?!” Hegel rolled away and scrambled to his feet, peering about blearily.</p>
<p>“Sleepin on watch.” Manfried shook his head. “Shameful.”</p>
<p>“Who’s sleepin on watch? I woke you last, you bastard!”</p>
<p>“Liar, you dozed off your first turn at it.”</p>
<p>“I kicked you, you miserable goat!”</p>
<p>“When?”</p>
<p>“When I was done lookin out!”</p>
<p>“Hmmm.” Manfried chewed his beard, dimly recollecting a foot to his side in the depths of slumber. “Well, I suppose it’s no
         fault a either a us, then.”
      </p>
<p>“No fault? You sayin you didn’t get up at all? What the Hell, brother, that’s your fault clean and simple.”</p>
<p>“Should a made sure I was up,” Manfried grumbled, then brightened. “Fuck it all, Hegel, what’re we on about? There’s loot
         waitin just down the hill!”
      </p>
<p>Snatching seared pieces of meat, the two raced down the trail to the scene of the slaughter. Any nocturnal scavengers had
         left the bear’s share for the Brothers, who meticulously piled anything of worth in the middle of the trail. After a brief
         council, they plodded down the switchbacks to where Bertram had come to rest after his horse rode off the side of the sheer
         path. Defying the odds the hardy man still lived, although his splintered spine prevented him from moving anything more than
         his lips.
      </p>
<p>“Gross,” he mumbled through the wreckage of his face. “Gross bar.”</p>
<p>“Yeah,” Hegel allowed, “that’s us.”</p>
<p>“Tough, ain’t you?” Manfried was impressed.</p>
<p>“Bass,” the man wheezed. “Bass. Bass.”</p>
<p>“What’s that?” Hegel scowled, smelling a slander on the wind.</p>
<p>“Turds,” came out as a gurgle, Manfried experimentally pressing on Bertram’s chest with his heel. “Bastards.”</p>
<p>“Now, that’s hardly fair.” Hegel squatted in the dust. “We both recollect our father’s face, even if our mother didn’t.”</p>
<p>“He’s past pain, brother,” said Manfried, sliding off Bertram’s boot and poking his toes with a knife. “Look, he ain’t even
         flinchin.”
      </p>
<p>“Kill,” Bertram gasped. “Kill. Ill!”</p>
<p>“Who, you or us?” Hegel grinned and turned to his brother. “Tore up to death and still talkin vengeance! Not a bad sort, not
         at all.”
      </p>
<p>“Mercy, then?” Manfried asked. “I was dealin with old Cunter, so’s I didn’t see. Say his horse took’em over?”</p>
<p>“Yeah, the one we seen on the slope above, all busted up.” Hegel looked Bertram in his unswollen eye. “That’s you served proper
         for puttin faith in a beast. Should a dismounted, might a stood a chance.”
      </p>
<p>Bertram tried to spit but only drooled blood.</p>
<p>“Seen’em before?” Manfried asked, still absently cutting into Bertram’s foot.</p>
<p>“Can’t say that I recall’em from our small times.” Hegel scratched his beard. “On account a his cowardice in bringin a horse
         to a man-fight, I’s a mind to leave’em for the birds.”
      </p>
<p>“He didn’t run, though,” Manfried countered, having taken a shine to the man’s perseverance. “Didn’t cut out on his fellows
         like that other fuckscum. Didn’t try to get all dishonest with a bow, neither, and lived all night in the cold.”
      </p>
<p>“Still, brother, a horse? He meant to ride me down. Just think, Manfried, me, kilt by a goddamn horse!”</p>
<p>“A test, then,” said Manfried. He set down his knife and joined his brother in squatting by Bertram’s head. “You want mercy,
         coward?”
      </p>
<p>“Hell,” Bertram belched. “Die. Gross.”</p>
<p>“See?” Manfried smiled triumphantly at his brother. “Only a coward asks for mercy, even if it’s offered.”</p>
<p>“Pigshit,” said Hegel. “Only a mecky coward would lie on his ass while someone tickled his toes with a blade.”</p>
<p>“Assholes,” Bertram managed.</p>
<p>“Clear as day, he’s too broke to move anythin else. Watch.” Manfried prodded Bertram’s lips with his finger, and despite his
         agony the man snapped his teeth, desperate for even a drop of Grossbart blood.
      </p>
<p>“Well, alright,” Hegel relented, and smashed in Bertram’s skull with a rock.</p>
<p>They had little to show for their toil except for boots to replace their worn, pointed turnshoes, and actual weapons. Hegel
         claimed Gunter’s sword and Hans’s pick, while Hegel took Bertram’s mace and Helmut’s ax, leaving the one used on Heinrich’s
         wife in the road as a warning to any who came after. The few salvageable bolts they shoved into makeshift quivers; cudgels,
         dull knives, and several choice round stones were tossed in with the rest of their gear.
      </p>
<p>The clothing had suffered worse than the men who wore it, and not a corpse present had either coinage or jewelry. Bertram
         they covered in scree but the rest were unanimously judged to be cowards and thus crowfeed. Daylight showed the impracticality
         of attempting to maneuver the cart down the opposite slope, the trail diminishing to the point that even getting the horse
         down would prove daunting. The Grossbarts had faith, though, and loaded up the animal Manfried named “Horse” and Hegel dubbed
         “Stupid.”
      </p>
<p>Hegel applied ax to cart, further burdening the workhorse-turned-pack mule with all the firewood he could cram into the folds
         of blanket lashed onto its back. Then they started off, Manfried leading Horse down the mountainside. Although the path showed
         no signs of usage, they remained convinced it would soon join a wider road leading all the way through the mountains. They
         were wrong, of course, but did not learn this for some time. By noon they reached a wooded valley, and after plodding though
         the shade they climbed another rise and came to an even steeper pass late in the afternoon.
      </p>
<p>In the failing light they decided to camp at the bottom of the slope. Providence offered them a clearing split by a small
         stream, and they gathered wood to conserve the cart pieces for leaner times. Hegel unwrapped the horse head he had severed
         that morning and set to carving and stewing it for headcheese. Manfried caught frogs in the brook, but mid-autumn in the low-lands
         was early winter in the mountains, and the few specimens he found were sluggish and small. The chill brought on by night forced
         them close to the fire, but the Grossbarts’ morale rose with the stars as they discussed the days and weeks to come. One of
         the dead horses had yielded a cask full of rank beer and they shared it happily, laughing and swearing late into the dark.
         The cold ensured that one always stood watch to stoke the fire, and shortly before dawn they loaded up Horse, came out of
         the trees, and went up the next incline.
      </p>
<p>This pass came even higher, and after struggling upward for the better part of the morning they were afforded an unbroken
         view of pristine peaks before them and the foothills behind. Their exuberance dampened several hours later when they came
         down into an alpine meadow where the trail faded into the grass and could not be found again. The mount they had descended
         met another across the field, jabbing skyward high as the sun. After much cursing and accusations, they decided to continue
         on a roughly southern course, for somewhere beyond lay a wide and worn road leading all the way to the sea-lands. Another
         argument ended with the conclusion that a slower road with the option of horse meat down the path was superior to the instant
         gratification a quicker, more direct approach might yield.
      </p>
<p>Hegel laughed triumphantly each time Stupid slid on the rocks, but Manfried cooed to Horse and encouraged him to double his
         efforts. Eventually they crested the obstacle and were rewarded with an even more precarious descent to the next meadow. Here
         they dropped down exhausted, and did not rise until shadows coated the vale. Hegel assaulted the only tree to be found with
         an ax while Manfried kindled a fire and wiped down Horse.
      </p>
<p>The headcheese had grown ripe in Hegel’s pack, and they feasted on horsesteaks and brains as they debated theology. The stars
         shone and the wind blew, the Brothers enrapt in their discussion of Mary and Her ponce of a son. Hegel could not fathom how
         such a wonderful maiden had borne such a pusillanimous boy.
      </p>
<p>“Seems simple,” Manfried theorized. “After all, Ma was shit as shit can be, yet we’s immaculate.”</p>
<p>“True words.” Hegel nodded. “But it’s natural for fine crops to spring from mecky earth, so we’s not so much a anomaly as
         a rare, decent woman birthin heel stead a hero.”
      </p>
<p>“He took his lumps, though. Didn’t squeal none.”</p>
<p>“So what? Not puttin up a fuss when you’s gettin stuck up on a cross don’t seem honest to me. He could a kicked one a them,
         at the very goddamn least.”
      </p>
<p>“I’s not quarrelin that point.”</p>
<p>“Only cause you can’t, you contrary cunt. Suppose you could go on about it bein braver to let’em torture you to death but
         we both know that don’t wash.”
      </p>
<p>“Is damn strange, though. Seems someone must a closed their ears at some point in the tale and got it all crooked when it
         came out again. She’s the bride a the Lord, yet She’s a virgin. A virgin what gets with foal. Then She gives birth to Her
         husband.”
      </p>
<p>Hegel chortled. “Guess he got in there after all!”</p>
<p>“Watch your blasphemous tongue,” snapped Manfried, tugging his beard. “Had you the sense to listen you’d hear how I got it
         all figured.”
      </p>
<p>“Oh you do, huh?”</p>
<p>“Damn right. See, one thinks She can’t be a virgin, cause virgins can’t have babes or they ain’t virgin. The Lord’s pole is
         pole nonetheless, Hell, if anythin, it’s the biggest pole to ever poke fold.”
      </p>
<p>Hegel unbunged the cask, reckoning they needed some sacramental beverage if they were to truly unravel the mystery.</p>
<p>“But She’s definitely a virgin, I mean, just look at Her.” Manfried held up the Virgin he had recently carved. All day he
         had waited for an excuse to show up his brother’s necklace.
      </p>
<p>“No question,” Hegel agreed, trading the beer for a better look at his brother’s handiwork.</p>
<p>“So here’s what I think. The Lord comes pokin his thing round Mary, bein all sweet and tryin to get him some a Her sweetness.
         And She straight denies him the privilege.”
      </p>
<p>“Why’d She do that?”</p>
<p>“To stay pure. Lord or man, She knew to stay holier than the rest She’d have to be virgin for all time, else She’d be just
         another mecky sinner.”
      </p>
<p>Hegel stared at the statue, contemplating this.</p>
<p>“So the Lord’s mad, real mad, as the Lord’s wont to do. So he sticks it to Her anyway.” Manfried belched.</p>
<p>“No!”</p>
<p>“Yes!”</p>
<p>“But couldn’t he, I dunno, make Her want to?”</p>
<p>“He tried! Everythin’s got limits, brother, and even the Lord can’t make a girl <em>want</em> to spread for him, even if he can force Her.”
      </p>
<p>“Poor Mary.”</p>
<p>“Don’t pity Her, cause She got Her revenge. Made sure the Lord’s son was the snivelingist, cuntiest, most craven coward in
         a thousand years.”
      </p>
<p>Enlightenment misted Hegel’s eyes. “She done that for <em>vengeance</em>?”
      </p>
<p>“Worst fate imaginable, havin a son like that. And that’s why She’s holy, brother. Out a all the folk the Lord tested and
         punished, She’s the only one who got him back, and worse than he got Her. That’s why She intercedes on our behalf, cause She
         loves thems what stand up to the Lord more than those kneelin to’em.”
      </p>
<p>“I understand <em>that</em>. But why’s She still called the Virgin?”
      </p>
<p>“Well Hell, everyone knows rape ain’t the same.”</p>
<p>“It ain’t?”</p>
<p>“Nah, you gotta want it. It’s fuckin spiritual.”</p>
<p>Hegel ruminated only a moment before his mind convinced his mouth that his brother was indeed in the wrong: “Nah.”</p>
<p>“Nah?”</p>
<p>“Nah.”</p>
<p>“Explain your fuckin <em>nah</em> or stand and deliver, you mouthy bastard!”
      </p>
<p>“Rape,” Hegel cleared his throat, “is the forcible takin a one’s purity through brute effort. Or in simpler speak for simpler
         ears, <em>only</em> a virgin can be raped, and she ain’t virgin once she’s had the business.”
      </p>
<p>“Seein’s how I happen to be dealin with a hollowhead, I’s prepared to overlook your disparagin view a my ears. As for rape
         bein constrained to those what still got their chaste goin on, let lone possible only on such, may I ask by whose oafish,
         misshapen mouth you gained this wisdom?”
      </p>
<p>“Jurgen was sayin—”</p>
<p>“Ah! Illumi-fuckin-nation! The same Jurgen what was so fond a tellin you the evils a liberatin the dead a their unused valuables,
         that ill-learned asshole?”
      </p>
<p>“Now Jurgen weren’t half bad!”</p>
<p>“Correct again, that sister-fuckin thief was all bad. Can’t trust a man what cleans his dirty junk in his ma’s mouth, regardless
         how fit she might appear to the unrelated eye.”
      </p>
<p>“That’s damn conjecture and you know it!”</p>
<p>“Jecture or no, don’t lend’em weight as a reliable font a knowledge.” Manfried adopted the northern accent of the accused
         incest practitioner: “<em>Only virgins kin git rapt</em>. <em>Gittin rapt means you ain’t virgin nah more</em>. Ashes to assholes that filth told you fuckin your own kin weren’t no sin, neither, eh?”
      </p>
<p>“No,” Hegel lied, and poorly at that.</p>
<p>“Well, who you trust is up to you,” Manfried sighed, “some forsaken degenerate or your own blood, sayin naught a the fuckin
         Virgin.”
      </p>
<p>“You know it ain’t like that, brother!”</p>
<p>“Then why’s we still talkin, eh?”</p>
<p>That was good enough for the both of them, and they bedded down for the night. A howling wolf somewhere deep in the mountains
         reminded them of the prudence of keeping watch and they passed another night in shifts. The sun found them where it had left
         them—mildly lost in the Alps.
      </p>
<p>Picking their way up and down the range for several days brought them no closer to the southern road, and after a minor squabble
         over whose sense of direction surpassed the other’s, they traveled southwest over the spines of great peaks, skirting their
         stony brows and plodding onward, always in search of the next pass. The weather grew meaner by the day, the winds slashing
         ever deeper through their coats. The grassy meadows diminished in size and frequency while the glaciers increased, and each
         night the baying of wolves seemed closer. The meat had run out and the turnips were growing scant, and while Manfried’s logic
         had thus far prevailed, they both appraised Horse hungrily by starlight.
      </p>
<p>After a week they clambered to the summit of a boulder field and surveyed a forest sprouting between two monstrous ridges.
         They scrambled down the scree, dragging the weary Horse behind them. Firewood, fresh water, protection from the wind, and
         hopefully meat awaited them. Birds circled the thick pines, and the shady Brothers were cheered to enter shadows after being
         exposed to the open sky for days on end. The silence of tombs enveloped them, and the naïve Brothers prayed they might even
         stumble upon an overgrown churchyard. The Virgin had delivered them into such a fine sanctuary the idea did not seem beyond
         reason.
      </p>
<p>“Mark me well,” Hegel cautioned, “them hill-dogs we’s been hearin is probably laid up somewhere in here.”</p>
<p>“Stands to reason,” Manfried agreed, scampering around the thick bushes that choked the wood. “Wolf meat’s better than none,
         though.”
      </p>
<p>A brook could be heard deeper in the copse, and when they finally found it among the twisted trunks they made camp nearby.
         Stretching out on the moss and drinking their fill, they realized they had burned most of their daylight; night comes on fearful
         quick in the mountains. They collected a huge pile of fallen limbs and underbrush but found no evidence of any animal they
         might catch for dinner. Hegel made a stew out of the last few turnips while his brother set snares along the stream, and even
         when the wind rocked the trees and howled through the crags above they remained comfortable.
      </p>
<p>“You want to sit first?” Manfried asked, pulling his blankets tight.</p>
<p>“Guess so.” Hegel set both crossbows beside the fire. They had salvaged only a dozen bolts, one of these having been removed
         from Hans’s groin. Hegel looked forward to trying out the heavy sword and pick, his brother curling up beside Bertram’s mace
         and his ax leaned against a tree. After Manfried began snoring, Hegel swigged the last bit of gutrot.
      </p>
<p>Night wore slowly under the trees, the canopy blotting out any stars or moonshine. The large fire provided ample light though,
         and nothing stirred in the wood. Just as Hegel felt his lids droop and reckoned he should wake his brother, a peculiar feeling
         crept over him.
      </p>
<p>In the course of their nefarious adventures neither Grossbart was a stranger to being hunted, yet time and again Hegel felt
         some inkling of when their pursuers drew close, and always knew when they were being watched. He kept such things to himself
         save when the situation necessitated it, and years earlier his uncle had declared him to possess the Witches’ Sight after
         Hegel suddenly urged they take cover just before a search party rounded the path they had walked. Hegel resented the term
         as any good Christian would, but his hunches always proved right.
      </p>
<p>The familiar raising of his hackles told him eyes watched from somewhere beyond the fire, and given the unbroken silence their
         owner must be soft of sole indeed. A more cautious and clever man might have feigned sleep to lure out the voyeur or slowly
         reached for a weapon. Such intelligent action would have meant disaster for both Grossbarts, so it is fortunate Hegel instead
         leaped to his feet as he notched a quarrel, shouting at the top of his lungs.
      </p>
<p>“Come out, you bastards!”</p>
<p>Manfried rolled out of his blankets and gained his feet, mace and ax at the ready.</p>
<p>“Got guests?” Manfried blinked his eyes, peering into the night.</p>
<p>“Don’t know,” Hegel shouted even louder. “Guests show themselves, honest-like! Only fools and fiends cower in the dark!”</p>
<p>A deep laugh rolled out of the blackness, and to Hegel’s shock it came from just behind him. He twisted around, crossbow leveled,
         but found no target. He aimed at where he thought the laughter emanated from but held his finger, wanting to make sure.
      </p>
<p>“Come over by the fire,” Hegel called a bit more softly. Manfried moved closer to his brother, squinting into the moonless
         forest.
      </p>
<p>“No thank you,” a voice growled from the dark, seeming to come from a throat choked with gravel. “Unless you care to douse
         that fire.”
      </p>
<p>Another chuckle that chilled the guts of both Brothers. They were accustomed to being the sinister voice in the night, and
         did not care to be on the receiving end of such a discourse. Manfried attempted to wrest control of the situation.
      </p>
<p>Taking a step forward, Manfried intoned, “May all those who love their salvation say evermore Mary is great!”</p>
<p>Another genuine belly laugh, and after a pause, that voice: “My mistress is far closer than that slattern, dwelling as she
         does in this very wood!”
      </p>
<p>“Fire your bow,” Manfried hissed.</p>
<p>Hands shaking, Hegel fired toward the voice. There was a skittering in the underbrush while Hegel clumsily reloaded, Manfried
         cocking his ear to pin down where the man was moving. Readied, Hegel raised the weapon but the silence persisted, only their
         breathing and the wind disturbing the stillness. Then they heard a swishing, like a switch being swung back and forth. Now
         the man must be even closer, somewhere just beyond the glow of the fire.
      </p>
<p>“Not Christian,” the man complained. “Come into my house and try to murder me.”</p>
<p>“See, it ain’t like that,” Hegel explained. “My finger slipped.”</p>
<p>The chortling bothered them more than the voice, and the faint whipping noise did not help.</p>
<p>“Slipped, did it? Oh, then it’s alright. After all, travelers in the night are right to be cautious, especially so deep in
         the wood, so far in the mountains. Never know who’s out there, prowling the night.”
      </p>
<p>“Right enough,” Manfried answered, sorely aware he did not need to yell to be heard.</p>
<p>“It’s been an awful long time,” said the man, “since we’ve had any visitors who’d talk to us.”</p>
<p>“That a fact?” Hegel swallowed, still trying to pinpoint the man’s location.</p>
<p>“Most just scream like children and run. Rather, they try to run.” Neither Grossbart found this warranted even a chuckle,
         let alone the drawn-out laugh that shook their nerves.
      </p>
<p>“We’s talkin,” Manfried pointed out. “Ain’t gonna run. Anyone runs, reckon it’ll be you.”</p>
<p>Hegel could not return his brother’s weak smile. “Yeah, uh, that’s how it is, friend.”</p>
<p>“Oh, I think I could make you run,” the voice growled. “Yes, I wager you’d run if you weren’t too scared to do nothing but
         mess your drawers and pray. All it’d take is me taking a few more steps toward that fire. Still want me to come into the light?
         Fair’s fair, here I come.”
      </p>
<p>“Nah, that’s alright,” Hegel quickly interjected. “You’s fine where you’s at, and we’s fine where we’s at, no sense in, uh,
         no sense in—”
      </p>
<p>“Forcin us to kill you,” Manfried finished, but the words almost stuck in his craw. He was no superstitious bumpkin but he
         knew dark things move at night, especially in the wilds where men rarely journey. Still, no sense in getting all frazzled.
         Sweat poured down his face despite the frigid night air. The chortling coming from the dark twisted his bowels, and his whole
         body shook with nervous excitement.
      </p>
<p>“Can’t have that,” the unseen interloper managed through his mirth. “My goodness, no.”</p>
<p>“Knew he was bluffin,” Manfried muttered, mouth dry and brow damp.</p>
<p>“Can’t have <em>you</em> killing <em>me</em>, that wouldn’t do at all. Have to put food on the board, yes?” the man rasped, only now his voice came from above them, drifting
         down out of the thick pine boughs. Manfried felt nauseous and light-headed, even his oversized ears failing to detect the
         movement in the dark.
      </p>
<p>“Yeah.” Hegel tried to keep his voice from quavering but he felt ill and weird. The Witches’ Sight—if that was truly what
         he possessed instead of mundane intuition—wracked his body with chills, every scrap of his skin itching to dash off into the
         night away from this clearly Mary-forsaken wood.
      </p>
<p>“So we’s decided,” Hegel finally said.</p>
<p>“Yes we are,” the voice almost whispered from the trees.</p>
<p>“You stay where you’s at and we stay where we’s at,” Hegel confirmed.</p>
<p>“Yes.”</p>
<p>“Good.” Hegel felt relieved.</p>
<p>“Until morning.”</p>
<p>“Til mornin?” Manfried bit his lip.</p>
<p>“When I fall upon you and eat you both alive.”</p>
<p>For the first time in their lives the Grossbarts were dumb-struck.</p>
<p>“You’ll scream then,” he continued, his voice rising with the wind. “You’ll beg and cry and I’ll suck the marrow from your
         bones before you expire. You’ll feel bits of you sliding into my belly still attached, and I’ll wear your skins when the weather
         turns.”
      </p>
<p>“Uh,” Hegel managed, looking like an occupant of the crypts from which they made their living.</p>
<p>Manfried could not even get that much out, eyes like saucers. His lips moved in prayer but no sound emerged. His faith that
         whoever waited outside their vision posed no serious hazard to them had dissipated. He wanted to spit in the face of whoever
         lurked in the trees, to say something so insulting it would make even his brother blush. What finally came out mirrored Hegel’s
         statement:
      </p>
<p>“Uh.”</p>
<p>Laughter rained down on them with such heartiness that pine needles accompanied it. The Brothers had subconsciously drawn
         so close that when their shoulders brushed they both jumped. No further sound came from the darkness, save the swishing both
         found familiar yet neither could place.
      </p>
<p>“Fire’s low,” Hegel whispered, the shadows lengthening on their periphery.</p>
<p>“So put wood on,” Manfried snapped. Neither had taken his eyes off the overhanging branches since the laughter had trailed
         off on the wind. They were uncertain whether moments or hours had passed, scanning the trees for movement. Hegel cracked first
         but used his feet to kick limbs onto the blaze, unwilling to set down his crossbow for even an instant.
      </p>
<p>“Watch my ass,” Manfried said, and retrieved the other arbalest. Stringing it, he rejoined his brother’s vigil. “Got an idea.
         Need to shoot soon as you see’em.” Manfried had lapsed into a guttural vernacular that only his brother could decipher. Their
         uncle grew furious whenever the Brothers adopted it, paranoid they were plotting against him. His suspicions were only occasionally
         justified.
      </p>
<p>“No need to say it twice,” Hegel replied in the same.</p>
<p>“Gotta stoke these flames, shine some light on matters,” Manfried announced to the wood, back in his regular Germanic mode
         of speech.
      </p>
<p>Dumping more branches on what quickly grew into a bonfire, Manfried suddenly leaped to his feet and hurled a flaming brand
         into the limbs overhead. Hegel stood ready but saw only the thick boughs of the pines. When the branch plummeted back down
         they avoided being singed by the hair of their beards.
      </p>
<p>“Damn,” they both said, Hegel looking right, Manfried looking left.</p>
<p>“Suppose he’s a ghost?” Hegel asked in their unique tongue.</p>
<p>“More likely a cannibal tryin to put the spook on us,” Manfried replied in kind.</p>
<p>“What’s a cannibal do all the way out here?”</p>
<p>“What you think he does? Eats people, told us himself.”</p>
<p>“Awful strange, be smart enough to talk but dumb enough to eat other folk stead a proper beasts. All they’s good for.” Hegel
         glanced at Stupid, who had calmed after the voice departed and stood dozing near the fire.
      </p>
<p>“Them crumbs you find in church is all cannibals, and they’s liable to talk you to death in the bargain.”</p>
<p>“What crumbs? What church?” asked Hegel.</p>
<p>“All a them. That’s what they eat, say it’s the body a Mary’s babe, and the wine’s his blood.”</p>
<p>“Oh, that rot again. Recollect that time we stole all a that hard bread and wine? That make us cannibals?”</p>
<p>“Hell no! Need a priest to turn it to flesh and blood.”</p>
<p>“Witchery,” Hegel judged it.</p>
<p>“It surely is. That’s how you know a man’s pure or not. Honest man don’t eat nobody else. Specially not no kin a Mary, I don’t
         care how much a bitchswine he is.”
      </p>
<p>“So you think whoever’s out there’s just a heretic?” Hegel felt relieved.</p>
<p>“Yeah, nuthin more nor less.” Manfried was not the least bit sure but it would not do to frighten his brother with speculation.
         “Besides, if he was somethin more than moonfruit what’s stoppin him from rushin us right now? Or earlier when I was asleep?”
      </p>
<p>“True words. Means to put the rattle on us, so’s we stay up all night and is half-strong come cockcrow.”</p>
<p>“Exactly.” Manfried heartened at Hegel’s sound point. “Any fool’ll tell you night’s when there’s real nastiness afoot. Nuthin
         I ever heard a prefers day to night cept ordinary people. So you get some rest, and I’ll stand guard.”
      </p>
<p>“I won’t hear it, brother, my watch had only begun when I roused you. I’ll stay up, you take in some shut-eye.”</p>
<p>“Nonsense. I can see from here your eyes are saggin and you’s got that tremor on your lip you always get when you’s tuckered.”</p>
<p>Hegel tried unsuccessfully to get a gander at his own mouth but his bulbous nose blotted out all but his lower beard. He reluctantly
         lay down, too out of sorts to argue anymore. He still felt hot and cold all over but could no longer be sure if this came
         from being watched or from exhaustion. He pretended to sleep for several hours, always keeping one eye half-cocked on the
         trees. He then switched places with Manfried, who did the same even less convincingly. Only Horse got any rest that night,
         and an hour before dawn both Grossbarts squatted beside the fire, crossbows ready, too tired to speak and without even a turnip
         to gnaw.
      </p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// SadTaleoftheBr_chap-3.vue?5e2aff2e